import { Navigate, Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { useCallback } from "react";
import { ThemeProvider, useColorScheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { createBrandTheme } from "brand";
import {
  CognitoAuthProvider,
  ProtectedRoute,
  CallbackPage,
  LogoutPage,
  LoggedOutPage,
  ActiveOrgProvider,
  BuildBadge,
  UsaBadge,
} from "common-fe";
import { OrganizationApi, Configuration } from "account-common";
import { useAuth } from "react-oidc-context";
import { AnnouncerProvider, useAnnouncerContext } from "./context/AnnouncerContext";
import AnnouncerRoleGate from "./components/AnnouncerRoleGate";
import InitialSetup from "./components/InitialSetup";
import OrderOfGo from "./components/OrderOfGo";

const ACCOUNT_API_BASE_URL = import.meta.env.VITE_ACCOUNT_API_BASE_URL;

// Inject the org-list fetcher (built from the generated account-common client)
// into the shared ActiveOrgProvider so announcer-fe sends the active-org
// X-Org-Id header on every score-api call — same pattern as scribe-fe /
// scoreadmin-fe. Without this the API falls back to the token's first org and
// an announcer with several org memberships would see the wrong show list.
const AnnouncerActiveOrgProvider = ({ children }) => {
  const auth = useAuth();
  const accessToken = auth.user?.access_token;
  const listOrgs = useCallback(
    async (isSuper) => {
      if (!accessToken) return [];
      const api = new OrganizationApi(
        new Configuration({ basePath: ACCOUNT_API_BASE_URL, accessToken })
      );
      const res = isSuper
        ? await api.listAllOrganizations()
        : await api.listOrganizations();
      return res.data?.status === "success" && Array.isArray(res.data.data)
        ? res.data.data
        : [];
    },
    [accessToken]
  );
  return <ActiveOrgProvider listOrgs={listOrgs}>{children}</ActiveOrgProvider>;
};

// Authenticated landing: an established session (show + ring already picked,
// sticky across reloads) skips straight to the Order of Go; otherwise land on
// /setup — same shape as scribe's LandingRoute.
function LandingRoute() {
  const { isSessionReady } = useAnnouncerContext();
  return <Navigate to={isSessionReady ? "/oog" : "/setup"} replace />;
}

function SessionRequiredRoute({ children }) {
  const { isSessionReady } = useAnnouncerContext();
  if (!isSessionReady) {
    return <Navigate to="/setup" replace />;
  }
  return children;
}

// Protected + role-gated + session-gated wrapper for the announcer screens.
function Scoped({ children }) {
  return (
    <ProtectedRoute>
      <AnnouncerRoleGate>
        <SessionRequiredRoute>{children}</SessionRequiredRoute>
      </AnnouncerRoleGate>
    </ProtectedRoute>
  );
}

function AppContent({ cognitoAuthConfig }) {
  const navigate = useNavigate();
  const location = useLocation();
  useColorScheme();

  return (
    <CognitoAuthProvider cognitoAuthConfig={cognitoAuthConfig} location={location}>
      <AnnouncerActiveOrgProvider>
        <AnnouncerProvider>
          <Routes>
            <Route
              path="/callback"
              element={<CallbackPage navigateFunction={navigate} location={location} />}
            />
            <Route path="/logout" element={<LogoutPage navigateFunction={navigate} />} />
            <Route path="/logged-out" element={<LoggedOutPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <AnnouncerRoleGate>
                    <LandingRoute />
                  </AnnouncerRoleGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/setup"
              element={
                <ProtectedRoute>
                  <AnnouncerRoleGate>
                    <InitialSetup />
                  </AnnouncerRoleGate>
                </ProtectedRoute>
              }
            />
            {/* #642 replaces this placeholder with the real Order of Go. */}
            <Route path="/oog" element={<Scoped><OrderOfGo /></Scoped>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AnnouncerProvider>
      </AnnouncerActiveOrgProvider>
    </CognitoAuthProvider>
  );
}

export default function App({ cognitoAuthConfig }) {
  const theme = createBrandTheme();
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppContent cognitoAuthConfig={cognitoAuthConfig} />
      <UsaBadge />
      <BuildBadge />
    </ThemeProvider>
  );
}
